import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { computed, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { db } from "@/firebase/config";
import { coordinateAuthorizationSignal } from "@/composables/authorizationRealtimeCoordinator";
import { initializeAuthorizationTitleAttention } from "@/composables/authorizationTitleAttention";

const LIFEWORTH_DOCUMENT_ID = process.env.VUE_APP_FIREBASE_LIFEWORTH_DOCUMENT_ID || "jB8HutcSXrdN51j1thic";
const REALTIME_ENABLED = process.env.VUE_APP_FIREBASE_REALTIME_ENABLED !== "false";

const numericId = value => Number(value?.id ?? value ?? 0);

const timestampMillis = value => {
  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  const parsed = Date.parse(value || "");
  return Number.isNaN(parsed) ? null : parsed;
};

export default function useAuthorizationRealtime() {
  const router = useRouter();
  const store = useStore();
  const user = computed(() => store.state.user);
  const titleAttention = initializeAuthorizationTitleAttention({ store, router });
  let unsubscribe = null;

  watch(
    () => `${user.value?.type || ""}:${user.value?.id || ""}:${numericId(user.value?.provider_id || user.value?.account)}`,
    () => titleAttention.reset()
  );

  const stop = () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  };

  const listenerContext = currentUser => {
    const modules = Array.isArray(currentUser?.modules) ? currentUser.modules : [];

    if (currentUser?.type === "hmo" && modules.includes("auth")) {
      return { audience: "hmo", identity: `hmo-${currentUser.id}` };
    }

    if (currentUser?.type === "provider" && (modules.includes("encounter") || modules.includes("billing"))) {
      const providerId = numericId(currentUser.provider_id || currentUser.account);
      if (providerId) {
        return { audience: `provider_${providerId}`, identity: `provider-${currentUser.id}-${providerId}` };
      }
    }

    return null;
  };

  const canReceive = (currentUser, payload) => {
    if (currentUser?.type === "provider") {
      return numericId(currentUser.provider_id || currentUser.account) === numericId(payload.provider_id);
    }

    const schemeIds = (currentUser?.schemes || []).map(numericId).filter(Boolean);
    return schemeIds.includes(numericId(payload.scheme_id));
  };

  const noticeText = (currentUser, payload) => {
    if (payload.action === "message_added") {
      return currentUser?.type === "hmo"
        ? `${payload.provider_name || "A provider"} added an encounter message.`
        : "HMO added a message to an encounter.";
    }

    const count = Number(payload.item_count || 0);
    const noun = count === 1 ? "authorization item" : "authorization items";

    if (currentUser?.type === "hmo") {
      if (payload.action === "withdrawn") {
        return `${payload.provider_name || "A provider"} withdrew ${count} ${noun}.`;
      }

      const message = payload.has_message ? " with a message" : "";
      return `${payload.provider_name || "A provider"} submitted ${count} ${noun}${message}.`;
    }

    return `HMO reviewed ${count} ${noun}.`;
  };

  const start = currentUser => {
    stop();

    if (!REALTIME_ENABLED || !LIFEWORTH_DOCUMENT_ID || !currentUser) {
      return;
    }

    const context = listenerContext(currentUser);
    if (!context) {
      return;
    }

    const cursorKey = `lifeworth-auth-signal:${context.identity}:${context.audience}`;
    let baselineEstablished = false;
    const latestSignal = query(
      collection(db, "lifeworth", LIFEWORTH_DOCUMENT_ID, context.audience),
      orderBy("created_at", "desc"),
      limit(1)
    );

    unsubscribe = onSnapshot(latestSignal, snapshot => {
      if (!baselineEstablished) {
        baselineEstablished = true;

        if (!snapshot.empty) {
          window.localStorage.setItem(cursorKey, snapshot.docs[0].id);
        }

        return;
      }

      if (snapshot.empty) {
        return;
      }

      const latest = snapshot.docs[0];
      const payload = { ...latest.data(), event_id: latest.id };
      const cursor = window.localStorage.getItem(cursorKey);

      if (cursor === latest.id) {
        return;
      }

      window.localStorage.setItem(cursorKey, latest.id);

      const expiresAt = timestampMillis(payload.expires_at);

      if ((expiresAt && expiresAt <= Date.now()) || !canReceive(currentUser, payload)) {
        return;
      }

      coordinateAuthorizationSignal({
        store,
        router,
        user: currentUser,
        event: {
        ...payload,
        message: noticeText(currentUser, payload),
        received_at: new Date().toISOString(),
        },
      });
    }, error => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("Authorization realtime listener failed.", error);
      }
    });
  };

  watch(
    () => {
      const currentUser = user.value;
      return currentUser ? JSON.stringify({
        id: currentUser.id,
        type: currentUser.type,
        provider: numericId(currentUser.provider_id || currentUser.account),
        modules: currentUser.modules || [],
        schemes: (currentUser.schemes || []).map(numericId),
      }) : "";
    },
    () => start(user.value),
    { immediate: true }
  );

  onUnmounted(stop);
  onUnmounted(() => titleAttention.stop());

  return { stop };
}
